// SEO helpers — manages document <title>, meta tags, and JSON-LD scripts.
//
// All operations are idempotent and key-based, so React strict mode double-mounts
// won't duplicate tags, and old values are restored on unmount.

import { useEffect } from 'react';
import { STORE } from './i18n';
import { assetUrl } from './api';

const META_KEY_ATTR = 'data-seo-key';

const ensureMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  return el;
};

const setMetaByName = (name, content) => {
  const el = ensureMeta(`meta[name="${name}"]`, { name });
  el.setAttribute('content', content || '');
  return el;
};

const setMetaByProperty = (property, content) => {
  const el = ensureMeta(`meta[property="${property}"]`, { property });
  el.setAttribute('content', content || '');
  return el;
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href || '');
  return el;
};

const setJsonLd = (key, data) => {
  const sel = `script[type="application/ld+json"][${META_KEY_ATTR}="${key}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute(META_KEY_ATTR, key);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return el;
};

const removeBySelector = (selector) => {
  document.head.querySelectorAll(selector).forEach(el => el.remove());
};

/**
 * Apply SEO for a product detail page. Returns a cleanup that restores defaults.
 */
export const useProductSEO = ({ product, lang }) => {
  useEffect(() => {
    if (!product) return undefined;

    const origin = (typeof window !== 'undefined' && window.location.origin) || '';
    const url = `${origin}/product/${product.id}`;

    const name = (lang === 'es' ? product.name_es : product.name_en) || product.name_en;
    const description = (lang === 'es' ? product.description_es : product.description_en) || product.description_en;
    const images = (product.images && product.images.length ? product.images : [product.image].filter(Boolean))
      .map(u => /^https?:\/\//.test(u) ? u : `${origin}${u.startsWith('/') ? '' : '/'}${u}`);
    const primaryImage = images[0] || '';

    const previousTitle = document.title;
    document.title = `${name} · ${STORE.name}`;

    setMetaByName('description', description);
    setLink('canonical', url);

    // Open Graph (used by WhatsApp, Facebook, Slack link previews)
    setMetaByProperty('og:type',          'product');
    setMetaByProperty('og:title',         name);
    setMetaByProperty('og:description',   description);
    setMetaByProperty('og:url',           url);
    setMetaByProperty('og:site_name',     STORE.name);
    if (primaryImage) setMetaByProperty('og:image', primaryImage);
    setMetaByProperty('product:price:amount',   String(product.price));
    setMetaByProperty('product:price:currency', 'USD');

    // Twitter Card
    setMetaByName('twitter:card',        primaryImage ? 'summary_large_image' : 'summary');
    setMetaByName('twitter:title',       name);
    setMetaByName('twitter:description', description);
    if (primaryImage) setMetaByName('twitter:image', primaryImage);

    // Product JSON-LD (Google Shopping organic listings)
    const productSchema = {
      '@context': 'https://schema.org/',
      '@type':    'Product',
      name,
      description,
      sku:        product.id,
      mpn:        product.model,
      brand:      { '@type': 'Brand', name: product.brand },
      image:      images.length ? images : undefined,
      category:   product.category,
    };
    // Include GTIN if present — Google strongly prefers GTIN for product matching
    if (product.gtin) {
      const len = String(product.gtin).replace(/\D/g, '').length;
      productSchema.gtin = product.gtin;
      if (len === 8 || len === 12 || len === 13 || len === 14) {
        productSchema[`gtin${len}`] = product.gtin;
      }
    }
    productSchema.offers = {
        '@type':         'Offer',
        url,
        priceCurrency:   'USD',
        price:           Number(product.price).toFixed(2),
        availability:    product.in_stock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/LimitedAvailability',
        itemCondition:   'https://schema.org/NewCondition',
        seller: {
          '@type':   'LocalBusiness',
          name:      STORE.name,
          telephone: STORE.phoneRaw,
          address: {
            '@type':            'PostalAddress',
            streetAddress:      '1015 Ogden Ave',
            addressLocality:    'Bronx',
            addressRegion:      'NY',
            postalCode:         '10452',
            addressCountry:     'US',
          },
        },
    };
    setJsonLd('product', productSchema);

    // Breadcrumb JSON-LD
    const breadcrumb = {
      '@context': 'https://schema.org/',
      '@type':    'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',           item: origin },
        { '@type': 'ListItem', position: 2, name: 'Shop',           item: `${origin}/shop` },
        { '@type': 'ListItem', position: 3, name: product.category, item: `${origin}/shop/${product.category}` },
        { '@type': 'ListItem', position: 4, name },
      ],
    };
    setJsonLd('breadcrumb', breadcrumb);

    return () => {
      document.title = previousTitle;
      removeBySelector(`script[type="application/ld+json"][${META_KEY_ATTR}="product"]`);
      removeBySelector(`script[type="application/ld+json"][${META_KEY_ATTR}="breadcrumb"]`);
      // Reset OG/Twitter to safe defaults so they don't bleed into other pages
      setMetaByProperty('og:type',  'website');
      setMetaByProperty('og:title', STORE.name);
      removeBySelector('meta[property="product:price:amount"]');
      removeBySelector('meta[property="product:price:currency"]');
    };
  }, [product, lang]);
};

/**
 * Apply LocalBusiness JSON-LD on the landing page.
 */
export const useLandingSEO = () => {
  useEffect(() => {
    const origin = (typeof window !== 'undefined' && window.location.origin) || '';

    setMetaByName('description', 'Bronx appliance store at 1015 Ogden Ave. Samsung, LG, Whirlpool, Bosch. Financing available. Visit us in store.');
    setLink('canonical', origin + '/');

    setMetaByProperty('og:type',        'website');
    setMetaByProperty('og:title',       STORE.name);
    setMetaByProperty('og:description', 'Bronx appliance store at 1015 Ogden Ave. Samsung, LG, Whirlpool, Bosch. Financing available. Visit us in store.');
    setMetaByProperty('og:url',         origin + '/');
    setMetaByProperty('og:site_name',   STORE.name);
    setMetaByProperty('og:image',       origin + '/img/storefront-og.jpg');
    setMetaByName('twitter:image',      origin + '/img/storefront-og.jpg');

    const local = {
      '@context':   'https://schema.org',
      '@type':      ['LocalBusiness', 'HomeGoodsStore'],
      name:         STORE.name,
      image:        origin + '/favicon.ico',
      url:          origin + '/',
      telephone:    STORE.phoneRaw,
      priceRange:   '$$',
      address: {
        '@type':         'PostalAddress',
        streetAddress:   '1015 Ogden Ave',
        addressLocality: 'Bronx',
        addressRegion:   'NY',
        postalCode:      '10452',
        addressCountry:  'US',
      },
      geo: {
        '@type':    'GeoCoordinates',
        latitude:   40.8345,
        longitude:  -73.9243,
      },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'], opens: '09:00', closes: '19:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday'], opens: '11:00', closes: '17:00' },
      ],
      sameAs: [],
    };
    setJsonLd('localbusiness', local);

    return () => {
      removeBySelector(`script[type="application/ld+json"][${META_KEY_ATTR}="localbusiness"]`);
    };
  }, []);
};

/**
 * Shop page OG / Twitter tags.
 *
 * Keeps the hard-coded index.html defaults for the home route and overrides
 * them only while /shop or /shop/:category is mounted. On unmount the home
 * defaults are restored so link previews stay accurate per route.
 */
export const useShopSEO = ({ lang, category } = {}) => {
  useEffect(() => {
    const origin = (typeof window !== 'undefined' && window.location.origin) || '';
    const path = category ? `/shop/${category}` : '/shop';
    const url = origin + path;

    const title = lang === 'es'
      ? 'Catálogo | Nathanael Appliances Corp'
      : 'Shop Catalog | Nathanael Appliances Corp';
    const description = lang === 'es'
      ? 'Tienda de electrodomésticos en el Bronx. Financiamiento disponible. Visítanos en 1015 Ogden Ave.'
      : 'Bronx appliance store. Financing available. Visit us at 1015 Ogden Ave.';
    const image = origin + '/img/storefront-og.jpg';

    const previousTitle = document.title;
    document.title = title;

    setMetaByName('description', description);
    setLink('canonical', url);

    setMetaByProperty('og:type',        'website');
    setMetaByProperty('og:site_name',   STORE.name);
    setMetaByProperty('og:title',       title);
    setMetaByProperty('og:description', description);
    setMetaByProperty('og:url',         url);
    setMetaByProperty('og:image',       image);

    setMetaByName('twitter:card',        'summary_large_image');
    setMetaByName('twitter:title',       title);
    setMetaByName('twitter:description', description);
    setMetaByName('twitter:image',       image);

    return () => {
      document.title = previousTitle;
      // Restore home-level OG defaults
      setMetaByProperty('og:title',       'Nathanael Appliances Corp | Bronx Appliance Store');
      setMetaByProperty('og:description', 'Bronx appliance store at 1015 Ogden Ave. Samsung, LG, Whirlpool, Bosch. Financing available. Visit us in store.');
      setMetaByProperty('og:url',         origin + '/');
    };
  }, [lang, category]);
};

